/**
 * Generate Client Bill - Billing Module
 * Select client, view services, enter unit prices, and generate invoice
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Card,
  tokens,
  Button,
  Input,
  Field,
  Spinner,
} from '@fluentui/react-components';
import { ReceiptRegular, DocumentRegular, DocumentBulletListRegular } from '@fluentui/react-icons';
import { SearchableDropdown } from '../../../components/common';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

interface Client {
  id: number;
  client_name: string;
}

interface ClientService {
  id: number;
  service_id: number;
  service_name: string;
  site_id: number;
  site_name: string;
  quantity: number;
  description: string;
}

interface BillItem extends ClientService {
  unit_price: number;
  total: number;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  headerIcon: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  card: {
    padding: '24px',
  },
  clientSelector: {
    maxWidth: '400px',
    marginBottom: '24px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '16px',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  thRight: {
    padding: '12px 16px',
    textAlign: 'right',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  tdRight: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
    textAlign: 'right',
  },
  priceInput: {
    width: '120px',
  },
  totalRow: {
    backgroundColor: '#faf9f8',
    fontWeight: '600',
  },
  grandTotal: {
    backgroundColor: tokens.colorBrandBackground,
    color: 'white',
  },
  grandTotalText: {
    fontSize: '18px',
    fontWeight: '700',
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #e1dfdd',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    gap: '12px',
  },
});

const GenerateClientBillFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [_clientServices, setClientServices] = useState<ClientService[]>([]);
  const [billItems, setBillItems] = useState<BillItem[]>([]);
  const [_loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchClientServices();
    } else {
      setClientServices([]);
      setBillItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-clients');
      if (response.data.status === 'success') {
        setClients(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const fetchClientServices = async () => {
    if (!selectedClient) {
      return;
    }
    setLoadingServices(true);
    try {
      const response = await ApiClient.get('/get-client-services', {
        params: { client_id: selectedClient },
      });
      if (response.data.status === 'success') {
        const services = response.data.data || [];
        setClientServices(services);
        setBillItems(
          services.map((s: ClientService) => ({
            id: s.id || 0,
            service_id: s.service_id || 0,
            service_name: s.service_name || '',
            site_id: s.site_id || 0,
            site_name: s.site_name || '',
            quantity: s.quantity || 1,
            description: s.description || '',
            unit_price: 0,
            total: 0,
          }))
        );
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoadingServices(false);
  };

  const handlePriceChange = (index: number, value: string) => {
    const price = parseFloat(value) || 0;
    setBillItems(prev => {
      const updated = [...prev];
      const item = updated[index];
      if (item) {
        updated[index] = {
          ...item,
          unit_price: price,
          total: item.quantity * price,
        };
      }
      return updated;
    });
  };

  const subtotal = useMemo(() => {
    return billItems.reduce((sum, item) => sum + item.total, 0);
  }, [billItems]);

  const handleGenerateInvoice = async () => {
    if (!selectedClient) {
      newToast('error', 'Please select a client');
      return;
    }

    const itemsWithPrices = billItems.filter(item => item.unit_price > 0);
    if (itemsWithPrices.length === 0) {
      newToast('error', 'Please enter at least one unit price');
      return;
    }

    setGenerating(true);
    try {
      const response = await ApiClient.post('/create-invoice', {
        client_id: parseInt(selectedClient),
        items: itemsWithPrices.map(item => ({
          client_service_id: item.id,
          service_id: item.service_id,
          site_id: item.site_id,
          service_name: item.service_name,
          site_name: item.site_name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
        })),
      });

      if (response.data.status === 'success') {
        newToast('success', 'Invoice generated successfully');
        navigate(`/account/billing/invoice-preview/${response.data.data.id}`);
      } else {
        newToast('error', response.data.message || 'Failed to generate invoice');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setGenerating(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
    }).format(amount);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <ReceiptRegular className={styles.headerIcon} />
          <div className={styles.headerText}>
            <Text className={styles.title}>Generate Client Bill</Text>
            <Text className={styles.subtitle}>
              Select a client, enter unit prices for services, and generate an invoice
            </Text>
          </div>
        </div>
        <Button
          appearance="primary"
          icon={<DocumentBulletListRegular />}
          onClick={() => navigate('/account/billing/invoices')}
        >
          View Invoices
        </Button>
      </div>

      <Card className={styles.card}>
        <div className={styles.clientSelector}>
          <Field label="Select Client" required>
            <SearchableDropdown
              options={clients.map(c => ({ id: c.id.toString(), label: c.client_name }))}
              selectedValue={selectedClient}
              onSelect={setSelectedClient}
              placeholder="Search and select a client..."
            />
          </Field>
        </div>

        {loadingServices ? (
          <div className={styles.loadingContainer}>
            <Spinner size="small" />
            <Text>Loading client services...</Text>
          </div>
        ) : !selectedClient ? (
          <div className={styles.emptyState}>
            <DocumentRegular style={{ fontSize: '48px', color: '#c8c6c4', marginBottom: '16px' }} />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              Select a Client
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              Choose a client from the dropdown to view their services
            </Text>
          </div>
        ) : billItems.length === 0 ? (
          <div className={styles.emptyState}>
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No Services Found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              This client has no assigned services to bill
            </Text>
          </div>
        ) : (
          <>
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th className={styles.th}>Service</th>
                  <th className={styles.th}>Site</th>
                  <th className={styles.thRight}>Quantity</th>
                  <th className={styles.thRight}>Unit Price (GHS)</th>
                  <th className={styles.thRight}>Total</th>
                </tr>
              </thead>
              <tbody>
                {billItems.map((item, index) => (
                  <tr key={item.id} className={styles.tr}>
                    <td className={styles.td}>{item.service_name}</td>
                    <td className={styles.td}>{item.site_name || '—'}</td>
                    <td className={styles.tdRight}>{item.quantity}</td>
                    <td className={styles.tdRight}>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className={styles.priceInput}
                        value={item.unit_price > 0 ? item.unit_price.toString() : ''}
                        onChange={e => handlePriceChange(index, e.target.value)}
                        placeholder="0.00"
                      />
                    </td>
                    <td className={styles.tdRight}>{formatCurrency(item.total)}</td>
                  </tr>
                ))}
                <tr className={`${styles.tr} ${styles.grandTotal}`}>
                  <td colSpan={4} className={styles.tdRight}>
                    <span className={styles.grandTotalText}>Grand Total</span>
                  </td>
                  <td className={styles.tdRight}>
                    <span className={styles.grandTotalText}>{formatCurrency(subtotal)}</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className={styles.actions}>
              <Button
                appearance="primary"
                icon={<ReceiptRegular />}
                onClick={handleGenerateInvoice}
                disabled={generating || subtotal === 0}
              >
                {generating ? 'Generating...' : 'Generate Invoice'}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default GenerateClientBillFluentUI;
